import { AfterViewInit, Component, OnChanges, SimpleChanges, ChangeDetectionStrategy, input } from '@angular/core';
import {CoilSelectionCriteria} from '../../model/coil-selection-criteria';
import {FluidType} from '../../model/fluid-type';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {SelectionItem} from '../../selections/selection-item';
import {StringUtils} from '../../utils/string-utils';
import {ConditionValueLimit} from '../../model/condition-value-limit';
import { FormsModule } from '@angular/forms';


@Component({
    selector: 'app-heating-performance-inputs',
    templateUrl: './heating-performance-inputs.component.html',
    styleUrls: ['./heating-performance-inputs.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule]
})
export class HeatingPerformanceInputsComponent implements OnChanges, AfterViewInit {

  readonly criteriaInput = input.required<CoilSelectionCriteria>({ alias: 'criteria' });
  readonly fluidTypes = input.required<FluidType[]>();
  readonly metricInput = input.required<boolean>();
  readonly showHydronicHeating = input.required<boolean>();
  readonly isSteam = input.required<boolean>();
  readonly invalidCapacity = input.required<boolean>();
  readonly conditionLimits = input.required<ConditionValueLimit[]>();
  readonly airflowInput = input.required<number>({ alias: 'airflow' });
  private airflow!: number;
  deltaValue!: number;
  deltaType: string;
  targetTypes!: SelectionItem[];

  constructor() {
    this.deltaType = 'tempChange';
  }

  ngAfterViewInit(): void {
    if (this.metricInput()) {
      this.convertConditionalValues();
    }
    this.targetTypes = this.getTargetTypes();
    this.criteriaInput().targetType = 'LEAVING_AIR_TEMPERATURE';
    this.targetTypeChanged();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['airflowInput']) {
      this.airflow = this.airflowInput();
    }
    if (changes['metricInput'] && !changes['metricInput'].firstChange) {
      if (!this.isSteam()) {
        if ('tempChange' === this.deltaType) {
          this.criteriaInput().temperatureChange = this.metricInput() ? 10 : 20;
          this.deltaValue = this.criteriaInput().temperatureChange;
          this.criteriaInput().fluidFlowRate = undefined!;
        } else {
          this.criteriaInput().fluidFlowRate = this.metricInput() ? 0.2 : 3;
          this.deltaValue = this.criteriaInput().fluidFlowRate;
          this.criteriaInput().temperatureChange = undefined!;
        }
      } else {
        this.criteriaInput().temperatureChange = undefined!;
        this.criteriaInput().fluidFlowRate = undefined!;
      }
      this.convertConditionalValues();
    }
    if (changes['criteriaInput']?.firstChange) {
      this.deltaValue = this.criteriaInput().temperatureChange;
      this.criteriaInput().fluidFlowRate = undefined!;
    }
  }

  getHeatingDeltaUnits(): string {
    return 'tempChange' === this.deltaType
      ? UnitConversionUtils.getTemperatureUnits(this.metricInput())
      : UnitConversionUtils.getFlowRateUnits(this.metricInput());
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.metricInput());
  }

  getPressureUnits(): string {
    return UnitConversionUtils.getSteamPressureUnits(this.metricInput());
  }

  isGlycol(fluidType: string): boolean {
    return 'E' === fluidType || 'P' === fluidType;
  }

  deltaChanged(): void {
    this.criteriaInput().temperatureChange = undefined!;
    this.criteriaInput().fluidFlowRate = undefined!;
    if ('tempChange' === this.deltaType) {
      this.criteriaInput().temperatureChange = this.deltaValue;
    } else {
      this.criteriaInput().fluidFlowRate = this.deltaValue;
    }
  }

  targetTypeChanged(): void {
    this.criteriaInput().targetValue = undefined!;
    if ('AIRFLOW' === this.criteriaInput().targetType && StringUtils.isEmptyNumericValue(this.criteriaInput().targetValue)) {
      this.criteriaInput().targetValue = this.airflow;
    }
  }

  getTargetTypeUnits(): string {
    switch (this.criteriaInput().targetType) {
      case 'LEAVING_AIR_TEMPERATURE':
        return UnitConversionUtils.getTemperatureUnits(this.metricInput());
      case 'TOTAL_CAPACITY':
      case 'SENSIBLE_CAPACITY':
        return UnitConversionUtils.getCapacityUnits(this.metricInput());
      case 'AIRFLOW':
        return UnitConversionUtils.getAirflowUnits(this.metricInput());
    }
    return undefined!;
  }

  private convertConditionalValues(): void {
    this.airflow = this.convertAndRoundAirflow(this.airflow);
    this.criteriaInput().dryBulbTemperature = this.convertAndRoundTemperature(this.criteriaInput().dryBulbTemperature);
    if (this.isSteam()) {
      this.criteriaInput().fluidTemperature = this.convertAndRoundSteamPressure(this.criteriaInput().fluidTemperature);
    } else {
      this.criteriaInput().fluidTemperature = this.convertAndRoundTemperature(this.criteriaInput().fluidTemperature);
    }
    switch (this.criteriaInput().targetType) {
      case 'LEAVING_AIR_TEMPERATURE':
        this.criteriaInput().targetValue = this.convertAndRoundTemperature(this.criteriaInput().targetValue);
        break;
      case 'TOTAL_CAPACITY':
      case 'SENSIBLE_CAPACITY':
        this.criteriaInput().targetValue = this.convertAndRoundCapacity(this.criteriaInput().targetValue);
        break;
      case 'AIRFLOW':
        this.criteriaInput().targetValue = this.convertAndRoundAirflow(this.criteriaInput().targetValue);
    }
  }

  private convertAndRoundAirflow(input: number): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertAirflow(input, this.metricInput()), 3);
  }

  private convertAndRoundCapacity(input: number): number {
    if (isNaN(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertCapacityValue(input, this.metricInput()), 3);
  }

  private convertAndRoundTemperature(input: number): number {
    if (isNaN(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertTemperatureValue(input, this.metricInput()), 3);
  }

  private convertAndRoundSteamPressure(input: number): number {
    if (isNaN(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertSteamPressure(input, this.metricInput()), 3);
  }

  private round(value: string, digits: number): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(digits);
    return +roundedValue;
  }

  private getTargetTypes(): SelectionItem[] {
    const items: SelectionItem[] = [];
    items.push(this.buildSelectionItem('LEAVING_AIR_TEMPERATURE', 'Leaving Air Temp'));
    if (this.showHydronicHeating()) {
      items.push(this.buildSelectionItem('TOTAL_CAPACITY', 'Total Capacity'));
      items.push(this.buildSelectionItem('AIRFLOW', 'Airflow'));
    }
    return items;
  }

  private buildSelectionItem(value: string, description: string): SelectionItem {
    const item = new SelectionItem();
    item.code = value;
    item.description = description;
    return item;
  }

  isFieldInvalid(fieldName: string): boolean {
    let limits: ConditionValueLimit[] = [];
    switch (fieldName) {
      case 'FluidTemperature':
        if (StringUtils.isZeroOrEmpty(this.criteriaInput().fluidTemperature)) {
          return true;
        }
        if (this.isSteam()) {
          limits = this.conditionLimits().filter(limit => limit.description === 'Steam Heating Entering Steam Pressure');
          if (limits.length > 0) {
            const limit = limits[0];
            if (this.criteriaInput().fluidTemperature < limit.minValue || this.criteriaInput().fluidTemperature > limit.maxValue) {
              return true;
            }
          }
        } else if (this.showHydronicHeating()) {
          if (!StringUtils.isZeroOrEmpty(this.criteriaInput().dryBulbTemperature)) {
            if (this.criteriaInput().fluidTemperature <= this.criteriaInput().dryBulbTemperature) {
              return true;
            }
          }
        }
        return false;
      case 'DryBulbTemperature':
        if (StringUtils.isZeroOrEmpty(this.criteriaInput().dryBulbTemperature)) {
          return true;
        }
        if (this.showHydronicHeating()) {
          if (!StringUtils.isZeroOrEmpty(this.criteriaInput().fluidTemperature)) {
            if (this.criteriaInput().fluidTemperature <= this.criteriaInput().dryBulbTemperature) {
              return true;
            }
          }
        }
        limits = this.conditionLimits().filter(limit => !limit.coolingApplication && limit.fluidTypeCode === this.criteriaInput().fluidType && limit.description.endsWith('Dry Bulb Temperature'));
        if (limits.length > 0) {
          const limit = limits[0];
          if (this.criteriaInput().dryBulbTemperature < limit.minValue || this.criteriaInput().dryBulbTemperature > limit.maxValue) {
            return true;
          }
        }
        return false;
      case 'GlycolPercentage':
        if (StringUtils.isZeroOrEmpty(+this.criteriaInput().glycolPercent)) {
          return true;
        }
        limits = this.conditionLimits().filter(limit => !limit.coolingApplication && limit.fluidTypeCode === this.criteriaInput().fluidType && limit.description === 'Glycol Percentage');
        if (limits.length > 0) {
          const limit = limits[0];
          if (+this.criteriaInput().glycolPercent < limit.minValue || +this.criteriaInput().glycolPercent > limit.maxValue) {
            return true;
          }
        }
        return false;
      default:
        return false;
    }
  }

}
